import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Theme options
 */
export type Theme = 'light' | 'dark' | 'system';

/**
 * UI store state interface
 */
interface UIState {
  // Sidebar state
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Theme
  theme: Theme;

  // Modal state
  activeModal: string | null;
  modalData: unknown;

  // Loading states for specific operations
  loadingStates: Map<string, boolean>;

  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setTheme: (theme: Theme) => void;
  openModal: (modalId: string, data?: unknown) => void;
  closeModal: () => void;
  setLoading: (key: string, loading: boolean) => void;
  isLoading: (key: string) => boolean;
}

/**
 * Get the effective theme based on system preference
 */
export function getEffectiveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return theme;
}

/**
 * UI store for managing UI state.
 * Persists theme and sidebar preferences.
 */
export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial state
      sidebarOpen: true,
      sidebarCollapsed: false,
      theme: 'system',
      activeModal: null,
      modalData: null,
      loadingStates: new Map(),

      // Actions
      toggleSidebar: () =>
        set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),

      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),

      setTheme: (theme) => {
        set({ theme });
        // Apply theme to document
        const effectiveTheme = getEffectiveTheme(theme);
        document.documentElement.setAttribute('data-theme', effectiveTheme);
      },

      openModal: (modalId, data = null) =>
        set({ activeModal: modalId, modalData: data }),

      closeModal: () => set({ activeModal: null, modalData: null }),

      setLoading: (key, loading) =>
        set((state) => {
          const newMap = new Map(state.loadingStates);
          if (loading) {
            newMap.set(key, true);
          } else {
            newMap.delete(key);
          }
          return { loadingStates: newMap };
        }),

      isLoading: (key) => get().loadingStates.get(key) ?? false,
    }),
    {
      name: 'admin-ui',
      // Only persist theme and sidebar preferences
      partialize: (state) => ({
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    }
  )
);

/**
 * Initialize theme on app load
 */
export function initializeTheme() {
  const { theme } = useUIStore.getState();
  const effectiveTheme = getEffectiveTheme(theme);
  document.documentElement.setAttribute('data-theme', effectiveTheme);

  // Listen for system theme changes
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => {
      const currentTheme = useUIStore.getState().theme;
      if (currentTheme === 'system') {
        const newEffectiveTheme = getEffectiveTheme('system');
        document.documentElement.setAttribute('data-theme', newEffectiveTheme);
      }
    });
}
